import { inject, Injectable } from "@angular/core";
import { SubscriptionUrlParams } from "@globals/models/SubscriptionUrlParams";
import { BiDomService } from "@globals/services/bi-dom.service";
import { updatePreset } from "@primeng/themes";
import { ButtonDesignTokens } from "@primeng/themes/types/button";
import { finalize, take } from "rxjs";
import { DriftstatusUrlParams } from "../driftstatus/DriftstatusUrlParams";

@Injectable({
  providedIn: "root"
})
export class IframeModuleUrlParamsHandlerService {
  private readonly domService = inject(BiDomService);

  public processSubscriptionModuleUrlParams(urlParams: SubscriptionUrlParams) {
    // If custom CSS url provided, make sure to load that as the first thing
    if (urlParams.cssUrl) this.loadAndProcessCustomCssUrlParam(urlParams.cssUrl).subscribe();

    const cssStringParts: Array<string> = [];

    if (urlParams.backgroundColor) {
      cssStringParts.push(`
           .p-panel-content {
            background-color: ${urlParams.backgroundColor} !important;
           }
           `);
    }

    // Button styles will be updated using PrimeNG's design tokens api
    const btnDesignTokens: ButtonDesignTokens = {
      colorScheme: {
        light: {
          root: {
            primary: {},
            secondary: {}
          }
        }
      }
    };
    if (urlParams.btnColor) {
      btnDesignTokens.colorScheme.light.root.primary = {
        background: urlParams.btnColor,
        hoverBackground: urlParams.btnHoverColor || urlParams.btnColor,
        activeBackground: urlParams.btnHoverColor || urlParams.btnColor,
        borderColor: urlParams.btnColor,
        hoverBorderColor: urlParams.btnHoverColor || urlParams.btnColor,
        focusRing: {
          shadow: `0 0 0 0.125rem var(--p-surface-0), 0 0 0 0.25rem ${urlParams.btnHoverColor || urlParams.btnColor}, 0 0.0625rem 0.125rem #000`
        }
      };

      cssStringParts.push(`
           #reset-button > .p-button {
            color: ${urlParams.btnColor};
           }
          #reset-button > .p-button:hover, #reset-button > .p-button:focus, #reset-button > .p-button:active {
            background: ${urlParams.btnHoverColor || "#000"} ;
           }
           `);
    }

    if (!urlParams.btnColor && urlParams.btnHoverColor) {
      btnDesignTokens.colorScheme.light.root.primary = {
        hoverBackground: urlParams.btnHoverColor,
        activeBackground: urlParams.btnHoverColor,
        hoverBorderColor: urlParams.btnHoverColor,
        focusRing: {
          shadow: `0 0 0 0.125rem var(--p-surface-0), 0 0 0 0.25rem ${urlParams.btnHoverColor}, 0 0.0625rem 0.125rem #000`
        }
      };

      cssStringParts.push(`
          #reset-button > .p-button:hover, #reset-button > .p-button:focus {
            color: ${urlParams.btnHoverColor};
           }
           `);
    }

    if (urlParams.btnFontSize) {
      cssStringParts.push(`
           body .p-button {
            font-size: ${urlParams.btnFontSize}px !important;
           }
           `);
    }

    if (urlParams.btnFontColor) {
      btnDesignTokens.colorScheme.light.root.primary.color = urlParams.btnFontColor;
      btnDesignTokens.colorScheme.light.root.secondary.color = urlParams.btnFontColor;
      btnDesignTokens.link = {
        color: urlParams.btnFontColor,
        hoverColor: urlParams.btnFontColor
      };

      cssStringParts.push(`
           body .p-button, #show-disclaimer-text-button {
            color: ${urlParams.btnFontColor} !important;
           }
           `);
    }

    if (urlParams.backColor) {
      cssStringParts.push(`
           #reset-button > span {
            color: ${urlParams.backColor};
           }
           `);
    }

    if (Object.keys(btnDesignTokens).length > 0) {
      updatePreset({
        components: {
          button: btnDesignTokens
        }
      });
    }

    //#region Font
    if (urlParams.fontColor) {
      cssStringParts.push(`
               body .FieldLabel, body .sub-header {
                color: ${urlParams.fontColor};
               }
               `);
    }

    if (urlParams.fontSize) {
      cssStringParts.push(`
               body .FieldLabel, .sub-header {
                font-size: ${urlParams.fontSize}px !important;
               }
               `);
    }

    if (urlParams.inputFontSize) {
      cssStringParts.push(`
               body .p-inputtext {
                font-size: ${urlParams.inputFontSize}px !important;
               }
               `);
    }

    if (urlParams.fontFamily) {
      cssStringParts.push(`
               .subscription-panel .p-panel-header .header,
               .sub-header.enter-contact-info-text,
               .FieldLabel,
               #show-disclaimer-text-button,
               .subscriber-infos span,

               .p-card.p-component .p-card-content *,
                p,
                ${!urlParams.inputFontFamily ? "body .p-inputtext," : ""}
               .p-button .p-button-label {
                font-family: ${urlParams.fontFamily} !important;
               }
               `);
    }

    if (urlParams.inputFontFamily && !urlParams.fontFamily) {
      cssStringParts.push(`
                body, body .p-inputtext {
                font-family: ${urlParams.inputFontFamily} !important;
               }
               `);
    }

    if (urlParams.thankYouTextColor) {
      cssStringParts.push(`
                    body subscription-confirmed-container {
                    color: ${urlParams.thankYouTextColor} !important;
                   }
                   `);
    }

    if (urlParams.disclaimerTextColor) {
      cssStringParts.push(`
                    #show-disclaimer-text-button {
                    color: ${urlParams.disclaimerTextColor} !important
                    }
                `);
    }

    if (urlParams.fontUrl) this.domService.loadStyle(urlParams.fontUrl);

    //#endregion

    // Header Styles
    if (urlParams.headerSize) {
      cssStringParts.push(`
      .subscription-panel .p-panel-header .p-panel-title {
            font-size: ${urlParams.headerSize}px !important;
           }
           `);
    }

    if (urlParams.headerFontColor) {
      cssStringParts.push(`
            .subscription-panel .p-panel-header .header {
            color: ${urlParams.headerFontColor} !important;
           }
           `);
    }

    if (urlParams.headerColor) {
      cssStringParts.push(`
      .subscription-panel .p-panel-header {
            background-color: ${urlParams.headerColor} !important;
            box-shadow: 0 0 0.1875em 0.0625em ${urlParams.headerColor} !important;
           }
           `);
    }

    // Borders
    if (urlParams.borderColor) {
      cssStringParts.push(`.subscription-panel .p-panel.p-component {
        border-color: ${urlParams.borderColor} !important;
      }`);
    }
    if (urlParams.borders && (urlParams.borders.toString() === "0" || urlParams.borders.toString() === "false")) {
      cssStringParts.push(`.subscription-panel .p-panel.p-component {
        border: none !important;
      }`);
    }

    // Apply CSS
    this.domService.insertGlobalCss(cssStringParts.join(""));
  }

  public processDriftStatusModuleUrlParams(urlParams: DriftstatusUrlParams) {
    const panelTitle = document.querySelector("#drift-status-panel .p-panel-header .p-panel-title") as HTMLElement;

    if (urlParams.headerFontColor) panelTitle.style.color = urlParams.headerFontColor;
    if (urlParams.headerSize) panelTitle.style.fontSize = urlParams.headerSize + "px";

    if (urlParams.headerFont) panelTitle.style.fontFamily = urlParams.headerFont;

    if (urlParams.headerCaps) panelTitle.style.textTransform = "uppercase";

    if (urlParams.borderColor || urlParams.borders) {
      const panelComponent = document.querySelector("#drift-status-panel .p-panel.p-component") as HTMLElement;

      if (urlParams.borderColor) panelComponent.style.borderColor = urlParams.borderColor;

      // URL params are always strings so booleans should be handled that way
      if (urlParams.borders && (urlParams.borders.toString() === "0" || urlParams.borders.toString() === "false")) panelComponent.style.border = "none";
    }

    if (urlParams.fontUrl) this.domService.loadStyle(urlParams.fontUrl);

    if (urlParams.headerBg || urlParams.headerColor) {
      const panelHeader = document.querySelector("#drift-status-panel .p-panel-header") as HTMLElement;
      if (urlParams.headerBg) panelHeader.style.background = `url(${urlParams.headerBg})`;

      if (urlParams.headerColor) {
        panelHeader.style.backgroundColor = urlParams.headerColor;
        panelHeader.style.boxShadow = `0 0 0.1875em 0.0625em ${urlParams.headerColor}`;
      }
    }
  }

  public loadAndProcessCustomCssUrlParam(url: string) {
    return this.domService.loadStyle(url).pipe(
      take(1),
      finalize(() => {
        // Some customer might make use of the custom CSS variables we use and define them on the app root component. As this won't work, we must check if there are any CSS variables defined here and '
        // then use them to overwrite what's on the root element (body)
        const computedStylesForAppRoot = getComputedStyle(document.querySelector("iframe-modules-app"));
        const docRoot = document.querySelector(":root") as HTMLElement;

        if (computedStylesForAppRoot.getPropertyValue("--color-brand")) docRoot.style.setProperty("--p-primary-brand-color", computedStylesForAppRoot.getPropertyValue("--color-brand"));
        if (computedStylesForAppRoot.getPropertyValue("--color-brand-brighter"))
          docRoot.style.setProperty("--p-primary-brand-color-light", computedStylesForAppRoot.getPropertyValue("--color-brand-brighter"));

        if (computedStylesForAppRoot.getPropertyValue("--color-button-primary")) {
          docRoot.style.setProperty("--p-button-primary-background", computedStylesForAppRoot.getPropertyValue("--color-button-primary"));
          docRoot.style.setProperty(
            "--p-button-primary-focus-ring-shadow",
            `0 0 0 0.125rem var(--p-surface-0),0 0 0 0.25rem ${computedStylesForAppRoot.getPropertyValue("--color-button-primary")},0 0.0625rem 0.125rem #000`
          );
        }

        if (computedStylesForAppRoot.getPropertyValue("--color-button-primary-hover"))
          docRoot.style.setProperty("--p-button-primary-hover-background", computedStylesForAppRoot.getPropertyValue("--color-button-primary-hover"));

        const css = `
            .font-normal {
              font-weight: ${computedStylesForAppRoot.getPropertyValue("--font-weight-normal") ?? "var(--font-weight-normal)"} !important;
           }
          .font-bold {
              font-weight: ${computedStylesForAppRoot.getPropertyValue("--font-weight-bold") ?? "var(--font-weight-bold)"} !important;
          }
          `;
        this.domService.insertGlobalCss(css, "TEST-CSS");
      })
    );
  }

  public correctUrlColorParams(urlParams: DriftstatusUrlParams) {
    const urlParamsClone = { ...urlParams };

    urlParamsClone.referrer = window?.document?.referrer;

    if (urlParamsClone.titleColor && urlParamsClone.titleColor.indexOf("#") === -1) urlParamsClone.titleColor = "#" + urlParamsClone.titleColor;

    if (urlParamsClone.headerColor && urlParamsClone.headerColor.indexOf("#") === -1) urlParamsClone.headerColor = "#" + urlParamsClone.headerColor;

    if (urlParamsClone.headerFontColor && urlParamsClone.headerFontColor.indexOf("#") === -1) urlParamsClone.headerFontColor = "#" + urlParamsClone.headerFontColor;

    if (urlParamsClone.fontColor && urlParamsClone.fontColor.indexOf("#") === -1) urlParamsClone.fontColor = "#" + urlParamsClone.fontColor;

    if (urlParamsClone.mainColor && urlParamsClone.mainColor.indexOf("#") === -1) urlParamsClone.mainColor = "#" + urlParamsClone.mainColor;

    if (urlParamsClone.borderColor && urlParamsClone.borderColor.indexOf("#") === -1) urlParamsClone.borderColor = "#" + urlParamsClone.borderColor;

    return urlParamsClone;
  }
}
